import { useState, useEffect, useCallback } from 'react';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { CardNumberElement, CardCvcElement, CardExpiryElement } from '@stripe/react-stripe-js';
import { useTrpcClient } from '../contexts/TrpcContext';
import {
  PaymentFormData,
  PaymentMethodData,
  PaymentMethodError,
  PaymentMethodResponse,
  SavedCard
} from '../types/payment';
import {
  CARD_ERROR_MESSAGES,
} from '../constants/payment';
import { StripeCardNumberElementChangeEvent, StripeElementChangeEvent, StripeElements } from '@stripe/stripe-js';
import { loadDatadog, trackEventDatadog } from '../utils/datadog';
import { datadogLogs } from '@datadog/browser-logs';

export interface UsePaymentMethodOptions {
  entityId?: string;
  advertiserId?: number;
  onSaveCard?: (card: SavedCard) => void;
}

export interface UsePaymentMethodReturn {
  cardErrors: {
    cardNumber?: string;
    expiration?: string;
    cvc?: string;
  };
  isProcessing: boolean;
  cardBrand: string | null;
  cardComplete: boolean;
  cardNumberComplete: boolean;
  cardExpiryComplete: boolean;
  cardCvcComplete: boolean;
  savedCard: SavedCard | null;
  isLoading: boolean;

  formData: PaymentFormData;

  handleCardChange: (event: StripeElementChangeEvent) => void;
  handleCardError: (error: any) => void;
  handleSubmit: (values: PaymentFormData, cardFieldsEdited?: boolean, saveAsInvoiceAddress?: boolean) => Promise<PaymentMethodData | PaymentMethodError>;
  clearCard: () => void;
  resetCardCompletionStates: () => void;
  setFormField: (field: keyof PaymentFormData, value: any) => void;
  setFormData: (data: Partial<PaymentFormData>) => void;
  setCardFieldError: (field: 'cardNumber' | 'expiration' | 'cvc', error: string | undefined) => void;
  fetchExistingPaymentMethod: () => Promise<void>;

  hasExistingCard: boolean;
  canSubmit: boolean;
  elements: StripeElements;
  clearErrors: () => void;
}

export const usePaymentMethod = (options: UsePaymentMethodOptions): UsePaymentMethodReturn => {
  const { entityId, advertiserId, onSaveCard } = options;

  if (!entityId && !advertiserId) {
    throw new Error('usePaymentMethod requires advertiserId or entityId');
  }
  const stripe = useStripe();
  const elements = useElements();
  const [cardErrors, setCardErrors] = useState<{
    cardNumber?: string;
    expiration?: string;
    cvc?: string;
  }>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardBrand, setCardBrand] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [savedCard, setSavedCard] = useState<SavedCard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormDataState] = useState<PaymentFormData>({
    cardholderName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    last4: ''
  });

  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);

  const hasExistingCard = !!savedCard;
  const canSubmit = (cardNumberComplete && cardExpiryComplete && cardCvcComplete) || hasExistingCard;

  const saveCard = useCallback((card: SavedCard) => {
    setSavedCard(card);
    onSaveCard?.(card);
  }, [onSaveCard]);

  const client = useTrpcClient() as any;

  const getPaymentMethod = useCallback(async () => {
    const data: PaymentMethodResponse = advertiserId
      ? await client.getPaymentMethodByAdvertiser.query({ advertiser_id: advertiserId })
      : await client.getPaymentMethods.query({ entityId });

    if (data.paymentMethod) {
      const newFormData: PaymentFormData = {
        cardholderName: data.paymentMethod.billing_details.name,
        addressLine1: data.paymentMethod.billing_details.address.line1,
        addressLine2: data.paymentMethod.billing_details.address.line2,
        city: data.paymentMethod.billing_details.address.city,
        state: data.paymentMethod.billing_details.address.state,
        country: data.paymentMethod.billing_details.address.country,
        zipCode: data.paymentMethod.billing_details.address.postal_code,
        last4: data.paymentMethod.card.last4,
        expMonth: data.paymentMethod.card.exp_month,
        expYear: data.paymentMethod.card.exp_year,
      };

      setFormDataState(newFormData);

      setCardBrand(data.paymentMethod.card.brand);
      saveCard({
        brand: data.paymentMethod.card.brand,
        last4: data.paymentMethod.card.last4,
        expMonth: data.paymentMethod.card.exp_month,
        expYear: data.paymentMethod.card.exp_year,
        paymentMethodId: data.paymentMethod.id,
      });
      setCardComplete(true);
    } else {
      // No saved card - reset card state
      setSavedCard(null);
      setCardBrand(null);
      setCardComplete(false);
      setCardNumberComplete(false);
      setCardExpiryComplete(false);
      setCardCvcComplete(false);
      setCardErrors({});
    }
  }, [entityId, advertiserId, client, saveCard, setFormDataState, setCardBrand, setCardComplete]);

  const fetchExistingPaymentMethod = useCallback(async () => {
    try {
      setIsLoading(true);
      await getPaymentMethod();
    } catch (error) {
      console.error('[usePaymentMethod] Error fetching existing payment method:', error);
    } finally {
      setIsLoading(false);
    }
  }, [entityId, advertiserId, getPaymentMethod]);

  useEffect(() => {
    if (entityId || advertiserId) {
      fetchExistingPaymentMethod();
    }
  }, [entityId, advertiserId, fetchExistingPaymentMethod]);

  useEffect(() => {
    if (cardNumberComplete && cardExpiryComplete && cardCvcComplete) {
      setCardComplete(true);
    } else {
      setCardComplete(false);
    }
  }, [cardNumberComplete, cardExpiryComplete, cardCvcComplete]);

  const setFormField = useCallback((field: keyof PaymentFormData, value: any) => {
    setFormDataState(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const setCardFieldError = useCallback((field: 'cardNumber' | 'expiration' | 'cvc', error: string | undefined) => {
    setCardErrors(prev => ({
      ...prev,
      [field]: error
    }));
  }, []);

  const handleCardChange = useCallback((event: StripeElementChangeEvent) => {
    if (event.elementType === 'cardNumber') {
      setCardFieldError('cardNumber', event.error ? event.error.message : undefined);
      const ev = event as StripeCardNumberElementChangeEvent;
      setCardNumberComplete(event.complete);
      if (ev.brand) {
        setCardBrand(ev.brand);
      }
      if (ev.complete) {
        setFormField('last4', '****');
      }
    } else if (event.elementType === 'cardExpiry') {
      setCardFieldError('expiration', event.error ? event.error.message : undefined);
      setCardExpiryComplete(event.complete);
    } else if (event.elementType === 'cardCvc') {
      setCardFieldError('cvc', event.error ? event.error.message : undefined);
      setCardCvcComplete(event.complete);
    }
  }, [setCardFieldError, setFormField]);

  const handleCardError = useCallback((error: any) => {
    // Map Stripe error codes to specific card fields
    if (error.code) {
      switch (error.code) {
        case 'card_number_invalid':
        case 'card_declined':
        case 'incorrect_number':
          setCardErrors(prev => ({ ...prev, cardNumber: error.message }));
          break;
        case 'card_expiry_invalid':
        case 'invalid_expiry_month':
        case 'invalid_expiry_year':
          setCardErrors(prev => ({ ...prev, expiration: error.message }));
          break;
        case 'card_cvc_invalid':
        case 'incorrect_cvc':
          setCardErrors(prev => ({ ...prev, cvc: error.message }));
          break;
        default:
          // For unknown error codes, show on card number field as default
          setCardErrors(prev => ({ ...prev, cardNumber: error.message }));
      }
    } else {
      // If no error code, show on card number field as default
      setCardErrors(prev => ({ ...prev, cardNumber: CARD_ERROR_MESSAGES.default }));
    }

    // Still throw the error for the modal to handle
    throw new Error(error.message || CARD_ERROR_MESSAGES.default);
  }, []);

  const setFormData = useCallback((data: Partial<PaymentFormData>) => {
    setFormDataState(prev => ({
      ...prev,
      ...data
    }));
  }, []);

  const clearCard = useCallback(() => {
    setSavedCard(null);
    setCardBrand(null);
    setCardComplete(false);
    setCardNumberComplete(false);
    setCardExpiryComplete(false);
    setCardCvcComplete(false);
    setFormField('last4', undefined);

    setCardErrors({});

    setFormDataState({
      cardholderName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      country: '',
      zipCode: '',
      last4: ''
    });
  }, [setFormField, setFormDataState, setCardErrors, setSavedCard]);

  const clearErrors = useCallback(() => {
    setCardErrors({});
  }, []);

  const resetCardCompletionStates = useCallback(() => {
    setCardComplete(false);
    setCardNumberComplete(false);
    setCardExpiryComplete(false);
    setCardCvcComplete(false);
    clearErrors();
  }, [clearErrors]);

  const handleSubmit = useCallback(async (values: PaymentFormData, cardFieldsEdited = false, saveAsInvoiceAddress = false): Promise<PaymentMethodData | PaymentMethodError> => {
    if (!stripe || !elements) {
      const error = 'Stripe has not been initialized';
      throw new Error(error);
    }

    if (!entityId && !advertiserId) {
      const error = 'Entity ID or Advertiser ID is required';
      throw new Error(error);
    }

    const requiredFields: (keyof PaymentFormData)[] = [
      'cardholderName', 'addressLine1', 'city', 'state', 'country', 'zipCode'
    ];

    for (const field of requiredFields) {
      if (!values[field]) {
        const error = `Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`;
        throw new Error(error);
      }
    }

    const cardFieldsComplete = cardNumberComplete && cardExpiryComplete && cardCvcComplete;

    if ((!hasExistingCard && !cardFieldsComplete) || (hasExistingCard && cardFieldsEdited && !cardFieldsComplete)) {
      const error = 'Please complete all card details';
      if (!cardNumberComplete) setCardFieldError('cardNumber', error);
      if (!cardExpiryComplete) setCardFieldError('expiration', error);
      if (!cardCvcComplete) setCardFieldError('cvc', error);
      throw new Error(error);
    }

    setIsProcessing(true);

    try {
      if (hasExistingCard && !cardFieldsEdited) {
        if (!savedCard?.paymentMethodId) {
          const error = 'No payment method ID found for update';
          console.error('[usePaymentMethod]', error);
          throw new Error(error);
        }

        try {
          if (advertiserId) {
            if (saveAsInvoiceAddress) {
              await client.savePaymentAddressByAdvertiser.mutate({
                advertiser_id: advertiserId,
                name: formData.cardholderName,
                address_line1: formData.addressLine1,
                address_line2: formData.addressLine2,
                address_city: formData.city,
                address_state: formData.state,
                address_zip: formData.zipCode,
                address_country: formData.country,
              });
            }
          } else {
            await client.updatePaymentMethod.mutate({
              paymentMethodId: savedCard.paymentMethodId,
              name: formData.cardholderName,
              address: {
                line1: formData.addressLine1,
                line2: formData.addressLine2 || undefined,
                city: formData.city,
                state: formData.state,
                postal_code: formData.zipCode,
                country: formData.country
              }
            });

            if (saveAsInvoiceAddress) {
              await client.updateInvoiceAddress.mutate({
                entity_id: entityId,
                update: {
                  entity_id: entityId,
                  billing_name: formData.cardholderName,
                  billing_address_1: formData.addressLine1,
                  billing_city: formData.city,
                  billing_state: formData.state,
                  billing_zip: formData.zipCode,
                  billing_country: formData.country,
                },
              });
            }
          }

          const paymentData: PaymentMethodData = {
            paymentMethodId: savedCard.paymentMethodId,
            cardDetails: {
              brand: savedCard?.brand || 'unknown',
              last4: savedCard?.last4 || '••••',
              expMonth: savedCard?.expMonth || 0,
              expYear: savedCard?.expYear || 0
            },
            billingDetails: formData
          };

          setFormDataState(formData);

          return paymentData;
        } catch (error) {
          console.error('[usePaymentMethod] Failed to update billing details:', error);
          throw new Error('Failed to update billing details');
        }
      }

      const cardNumberElement = elements.getElement(CardNumberElement);
      const cardExpiryElement = elements.getElement(CardExpiryElement);
      const cardCvcElement = elements.getElement(CardCvcElement);

      if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
        throw new Error('Card elements not found');
      }

      let paymentMethodId: string | null = null;
      let brand = cardBrand || 'unknown';
      let last4 = formData.last4 || '••••';
      let expMonth = savedCard?.expMonth || 0;
      let expYear = savedCard?.expYear || 0;

      if (advertiserId) {
        // Advertiser flow: create a card token and send it through Gary/BFF
        const tokResult = await stripe.createToken(cardNumberElement, {
          name: formData.cardholderName,
          address_line1: formData.addressLine1,
          address_line2: formData.addressLine2 || undefined,
          address_city: formData.city,
          address_state: formData.state,
          address_zip: formData.zipCode,
          address_country: formData.country,
        });

        if (tokResult.error || !tokResult.token) {
          handleCardError(tokResult.error);
          return { error: true, message: tokResult.error?.message || 'Failed to create card token' };
        }

        paymentMethodId = tokResult.token.id; // tok_...
        brand = tokResult.token.card?.brand || brand;
        last4 = tokResult.token.card?.last4 || last4;
        expMonth = tokResult.token.card?.exp_month || expMonth;
        expYear = tokResult.token.card?.exp_year || expYear;
      } else {
        // Entity flow: setup intent + confirmCardSetup
        const { clientSecret } = await client.setupIntent.mutate({
          entityId: entityId
        });

        const { error: setupError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardNumberElement,
            billing_details: {
              name: formData.cardholderName,
              address: {
                line1: formData.addressLine1,
                line2: formData.addressLine2 || undefined,
                city: formData.city,
                state: formData.state,
                postal_code: formData.zipCode,
                country: formData.country
              }
            },
            metadata: {
              entity_id: entityId
            }
          }
        });

        if (setupError) {
          handleCardError(setupError);
          return { error: true, message: setupError.message };
        }

        if (!setupIntent || !setupIntent.payment_method || typeof setupIntent.payment_method !== 'string') {
          throw new Error('Failed to setup payment method');
        }

        paymentMethodId = setupIntent.payment_method;
      }

      // Save payment method to backend (advertiser via Gary, entity via UBS)
      if (advertiserId) {
        await client.savePaymentMethodByAdvertiser.mutate({
          advertiser_id: advertiserId,
          token: paymentMethodId!,
        });
      } else {
        const updateResult = await client.updatePaymentMethod.mutate({
          paymentMethodId: paymentMethodId!,
          name: formData.cardholderName,
          address: {
            line1: formData.addressLine1,
            line2: formData.addressLine2 || undefined,
            city: formData.city,
            state: formData.state,
            postal_code: formData.zipCode,
            country: formData.country
          }
        });
      }

      if (saveAsInvoiceAddress) {
        if (advertiserId) {
          await client.savePaymentAddressByAdvertiser.mutate({
            advertiser_id: advertiserId,
            name: formData.cardholderName,
            address_line1: formData.addressLine1,
            address_line2: formData.addressLine2,
            address_city: formData.city,
            address_state: formData.state,
            address_zip: formData.zipCode,
            address_country: formData.country,
          });
        } else {
          await client.updateInvoiceAddress.mutate({
            entity_id: entityId,
            update: {
              entity_id: entityId,
              billing_name: formData.cardholderName,
              billing_address_1: formData.addressLine1,
              billing_city: formData.city,
              billing_state: formData.state,
              billing_zip: formData.zipCode,
              billing_country: formData.country,
            },
          });
        }
      }

      const paymentData: PaymentMethodData = {
        paymentMethodId: paymentMethodId!,
        cardDetails: {
          brand,
          last4,
          expMonth,
          expYear
        },
        billingDetails: formData
      };

      saveCard({
        brand,
        last4,
        expMonth,
        expYear,
        paymentMethodId: paymentMethodId!
      });

      await loadDatadog();

      await trackEventDatadog('payment_method_created', {
        paymentMethodId: paymentMethodId!,
        billingEntityId: advertiserId ?? entityId,
      });
      datadogLogs.logger.info('payment_method_created', {
        paymentMethodId: paymentMethodId!,
        billingEntityId: advertiserId ?? entityId,
      });

      await getPaymentMethod();

      return paymentData;
    } catch (err: any) {
      console.error('[usePaymentMethod] Payment Method Error:', err);
      const billingEntityId = entityId ?? advertiserId;
      trackEventDatadog('payment_method_created_error', {
        billingEntityId,
        error: err.message,
      });
      datadogLogs.logger.info('payment_method_created_error', {
        billingEntityId,
        error: err.message,
      });
      throw err;
    } finally {
      setIsProcessing(false);
    }
  }, [
    stripe,
    elements,
    client,
    entityId,
    advertiserId,
    formData,
    cardComplete,
    savedCard,
    cardBrand,
    hasExistingCard,
    cardNumberComplete,
    cardExpiryComplete,
    cardCvcComplete,
    handleCardError,
  ]);

  return {
    cardErrors,
    isProcessing,
    cardBrand,
    cardComplete,
    cardNumberComplete,
    cardExpiryComplete,
    cardCvcComplete,
    savedCard,
    isLoading,

    formData,

    handleCardChange,
    handleCardError,
    handleSubmit,
    clearCard,
    resetCardCompletionStates,
    clearErrors,
    setFormField,
    setFormData,
    setCardFieldError,
    fetchExistingPaymentMethod,
    elements,

    hasExistingCard,
    canSubmit
  };
};
